import { defineStore } from 'pinia';
import API from '@/shared/helpers/API.js';
import {useUserStore} from "@/console-new/stores/user.store";
import {useAblyStore} from "@/console-new/stores/ably.store";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {getLocalDateFromServer, formatDateTime} from "@/shared/helpers/dates";

export const useOpenAIChatsStore = defineStore('openaichats', {
	state: () => ({
		chats: {},
		loadingChats: false,
		activeChatId: null,
		pendingResponse: false,
		drawerOpen: false,
		hasUnreadMessage: false,
		attachedImages: [],
		showImagePicker: false,
	}),
	getters: {
		activeChat: state => {
			if(!state.activeChatId) {return null;}
			return state.chats?.[state.activeChatId] || null;
		},
		userStore: state => {
			return useUserStore();
		},
		ablyStore: state => {
			return useAblyStore();
		},
	},
	actions: {
		getChats(cb) {
			if(this.loadingChats) {
				if(cb) {cb(null)}
				return
			}
			this.loadingChats = true;
			API.init().getChats(this.userStore.currBusinessId, res=> {
				this.chats = {};
				this.loadingChats = false;
				if(res && res.result === 'OK' && res.chats) {
					res.chats.forEach((chat) => {
						let normalizedChat = this.normalizeChat(chat);
						this.chats[normalizedChat.ai_chat_id] = normalizedChat;
					})
					if(cb) {cb(this.chats)}
				} else {
					if(cb) {cb(null)}
				}
			});
		},
		newChat() {
			if(!this.userStore?.user?.user_id) {
				return
			}
			if(!this.chats['new']) {
				this.chats['new'] = {
					ai_chat_id: 'new',
					conversation_id: null,
					business_id: this.userStore.currBusinessId,
					user_id: this.userStore.user.user_id,
					created_at: formatDateTime(new Date()),
					updated_at: formatDateTime(new Date()),
					messages: [],
					name: useLanguageStore().getString('new_chat'),
				};
			}
			this.activeChatId = 'new';
		},
		createChat(message, images, cb) {
			if(this.loadingChats) {
				if(cb) {cb(null)}
				return
			}
			this.loadingChats = true;
			this.pendingResponse = true;
			this.newChat();
			// Add optimistic user message
			let userMessage = {
				role: 'user',
				request_data: message,
				created_at: new Date(),
			};
			if(images && images.length > 0) {
				userMessage.attachments = images;
			}
			this.chats['new'].messages.push(userMessage);
			API.init().createChat(this.userStore.currBusinessId, message, images, res=> {
				this.loadingChats = false;
				if(res && res.result === 'OK' && res.chat) {
					let normalizedChat = this.normalizeChat(res.chat);
					// Preserve the optimistic message
					normalizedChat.messages = this.chats['new'].messages;
					this.chats[normalizedChat.ai_chat_id] = normalizedChat;
					this.activeChatId = normalizedChat.ai_chat_id;
					delete this.chats.new;
					this.attachedImages = [];
					if(cb) {cb(normalizedChat)}
				} else {
					this.pendingResponse = false;
					delete this.chats.new;
					if(cb) {cb(null)}
				}
			});
		},
		sendMessage(chatId, message, images, cb) {
			if(this.loadingChats || this.pendingResponse) {
				if(cb) {cb(null)}
				return
			}
			this.loadingChats = true;
			this.pendingResponse = true;
			// Add optimistic user message
			let userMessage = {
				role: 'user',
				request_data: message,
				created_at: new Date(),
			};
			if(images && images.length > 0) {
				userMessage.attachments = images;
			}
			this.chats[chatId].messages = this.chats[chatId].messages.concat([userMessage]);
			API.init().sendChatMessage(this.userStore.currBusinessId, chatId, message, images, res=> {
				this.loadingChats = false;
				if(res && res.result === 'OK') {
					this.attachedImages = [];
					if(cb) {cb(true)}
				} else {
					this.pendingResponse = false;
					this.chats[chatId].messages.pop();
					if(cb) {cb(null)}
				}
			});
		},
		getChatMessages(chatId, cb) {
			if(this.loadingChats) {
				if(cb) {cb(null)}
				return
			}
			this.loadingChats = true;
			API.init().getChatMessages(this.userStore.currBusinessId, chatId, res=> {
				this.loadingChats = false;
				if(res && res.result === 'OK' && res.messages) {
					let normalizedMessages = res.messages.map(msg => this.normalizeMessage(msg));
					if(this.chats[chatId]) {
						this.chats[chatId].messages = normalizedMessages;
					}
					this.pendingResponse = res.pending || false;
					if(cb) {cb(normalizedMessages)}
				} else {
					if(cb) {cb(null)}
				}
			});
		},
		subscribeToChat(chatId) {
			if(!chatId || chatId === 'new') return;
			const channelKey = ENVIRONMENT + ':chat:' + this.userStore.currBusinessId + ':' + chatId;
			this.ablyStore.startAbly(() => {
				this.ablyStore.subscribeToChannel(channelKey, (message) => {
					if(message.name === 'chat-response') {
						this.handleChatResponse(chatId, message.data);
					}
				});
			});
		},
		unsubscribeFromChat(chatId) {
			if(!chatId || chatId === 'new') return;
			const channelKey = ENVIRONMENT + ':chat:' + this.userStore.currBusinessId + ':' + chatId;
			this.ablyStore.unsubscribeFromChannel(channelKey);
		},
		handleChatResponse(chatId, data) {
			// Handle failed responses
			if(data?.status === 'failed') {
				this.pendingResponse = false;
				useNotificationStore().showNotification('generic-fail');
				return;
			}

			// Mark as unread if drawer is closed
			if(!this.drawerOpen) {
				this.hasUnreadMessage = true;
			}

			// Refresh messages from the server
			API.init().getChatMessages(this.userStore.currBusinessId, chatId, res=> {
				if(res && res.result === 'OK' && res.messages) {
					let normalizedMessages = res.messages.map(msg => this.normalizeMessage(msg));
					if(this.chats[chatId]) {
						this.chats[chatId].messages = normalizedMessages;
					}
					this.pendingResponse = res.pending || false;
				}
			});
		},
		normalizeMessage(message) {
			if(message.ai_chat_response_id) {
				message.ai_chat_response_id = Number(message.ai_chat_response_id);
			}
			if(message.created_at && typeof message.created_at === 'string') {
				message.created_at = getLocalDateFromServer(message.created_at);
			}
			return message;
		},
		normalizeChat(chat) {
			chat.ai_chat_id = Number(chat.ai_chat_id);
			chat.business_id = Number(chat.business_id);
			chat.user_id = Number(chat.user_id);
			if(chat.created_at) {
				chat.created_at = getLocalDateFromServer(chat.created_at);
			}
			if(chat.updated_at) {
				chat.updated_at = getLocalDateFromServer(chat.updated_at);
			}
			chat.messages = chat.messages || [];
			return chat;
		},
		openDrawer() {
			this.drawerOpen = true;
			this.hasUnreadMessage = false;
		},
		closeDrawer() {
			this.drawerOpen = false;
		},
		toggleDrawer() {
			this.drawerOpen = !this.drawerOpen;
			if (this.drawerOpen) {
				this.hasUnreadMessage = false;
			}
		},
		addAttachedImage(imageUrl) {
			if(this.attachedImages.length < 3) {
				this.attachedImages.push(imageUrl);
			}
		},
		removeAttachedImage(index) {
			this.attachedImages.splice(index, 1);
		},
		clearAttachedImages() {
			this.attachedImages = [];
		},
		openImagePicker() {
			this.showImagePicker = true;
		},
		closeImagePicker() {
			this.showImagePicker = false;
		},
	}
});
